import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useLazyQuery } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import { TMS_TASKS, TmsTaskFilter, TmsTaskItem, TmsTasksResponse } from '../graphql/queries/taskManagementQueries';
import TaskWaypointDrawer from './TaskWaypointDrawer';
import TaskFieldEditDrawer from './TaskFieldEditDrawer';
import {
    BUSINESS_TYPE_MAP,
    DEFAULT_PAGINATION,
    IS_DELETED_OPTIONS,
    PAGE_SIZE_OPTIONS,
    ROUTE_TYPE_MAP,
    TASK_SOURCE_MAP,
    TASK_STATUS_OPTIONS,
    TASK_TYPE_MAP,
    parseIdInput,
    renderDeleted,
    renderTaskStatus,
    renderLocalWithTimezone,
    renderTagged,
    renderTime,
    renderText,
    toOptions,
} from './taskManagementShared';

const { RangePicker } = DatePicker;

type FilterValues = {
    id?: string;
    taskNumber?: string;
    routeId?: string;
    taskStatus?: number;
    taskType?: number;
    businessType?: number;
    taskDateRange?: [Dayjs | null, Dayjs | null] | null;
    isDeleted?: number;
};


type Props = {
    /** Tab2「查看任务详情」联动传入的任务ID：回填主键ID并按其查询 */
    linkedTaskId?: number;
    /** 联动触发序号（同一 ID 连续点击也要重新触发查询） */
    linkSeq?: number;
};

const TaskManagementTab: React.FC<Props> = ({ linkedTaskId, linkSeq }) => {
    const [form] = Form.useForm<FilterValues>();
    // 首次打开即按默认分页查全部（id 倒序，含已删除）；若由联动进入，初始条件即联动任务ID，避免先多查一次
    const [activeFilter, setActiveFilter] = useState<TmsTaskFilter>(() =>
        linkedTaskId !== undefined ? { id: linkedTaskId } : {},
    );
    const handledSeq = useRef<number | undefined>(undefined);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [waypointTask, setWaypointTask] = useState<{ id: number; taskNumber: string | null; businessType: number | null; taskStatus: number | null; routeType: number | null } | null>(null);
    const [editTarget, setEditTarget] = useState<{ id: number; taskNumber: string | null } | null>(null);
    const [runQuery, { data, loading }] = useLazyQuery<TmsTasksResponse>(TMS_TASKS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });

    useEffect(() => {
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [activeFilter, pagination, runQuery]);

    useEffect(() => {
        if (linkedTaskId === undefined || linkSeq === undefined) return;
        if (handledSeq.current === linkSeq) return;
        handledSeq.current = linkSeq;
        form.resetFields();
        form.setFieldsValue({ id: String(linkedTaskId) });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({ id: linkedTaskId });
    }, [linkedTaskId, linkSeq, form]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const id = parseIdInput(values.id);
        const routeId = parseIdInput(values.routeId);
        if (id === null || routeId === null) {
            message.warning('ID 必须是纯数字');
            return;
        }
        const taskNumber = values.taskNumber?.trim() || undefined;
        const [from, to] = values.taskDateRange ?? [null, null];
        const taskDateFrom = from ? from.format('YYYY-MM-DD') : undefined;
        const taskDateTo = to ? to.format('YYYY-MM-DD') : undefined;
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            id,
            taskNumber,
            routeId,
            taskStatus: values.taskStatus,
            taskType: values.taskType,
            businessType: values.businessType,
            taskDateFrom,
            taskDateTo,
            isDeleted: values.isDeleted,
        });
    };

    const columns: ColumnsType<TmsTaskItem> = [
        { title: 'ID', dataIndex: 'id', width: 90, fixed: 'left' },
        { title: '任务号', dataIndex: 'taskNumber', width: 160, fixed: 'left', render: renderText },
        { title: '状态', dataIndex: 'taskStatus', width: 120, render: renderTaskStatus },
        { title: '是否删除', dataIndex: 'isDeleted', width: 90, render: renderDeleted },
        { title: '任务日期', dataIndex: 'taskDate', width: 110, render: renderText },
        { title: '任务类型', dataIndex: 'taskType', width: 90, render: renderTagged(TASK_TYPE_MAP) },
        { title: '业务类型', dataIndex: 'businessType', width: 100, render: renderTagged(BUSINESS_TYPE_MAP) },
        { title: '来源', dataIndex: 'taskSource', width: 90, render: renderTagged(TASK_SOURCE_MAP) },
        { title: '路线ID', dataIndex: 'routeId', width: 90, render: renderText },
        { title: '路线代码', dataIndex: 'routeCode', width: 120, render: renderText },
        { title: '路线名称', dataIndex: 'routeName', width: 160, ellipsis: true, render: renderText },
        { title: '路线类型', dataIndex: 'routeType', width: 90, render: renderTagged(ROUTE_TYPE_MAP) },
        { title: '班次ID', dataIndex: 'scheduleId', width: 90, render: renderText },
        { title: '所属仓ID', dataIndex: 'warehouseId', width: 90, render: renderText },
        { title: '所属仓', dataIndex: 'warehouseName', width: 110, render: renderText },
        { title: 'BOL Number', dataIndex: 'bolNumber', width: 140, render: renderText },
        { title: '计划发车(UTC)', dataIndex: 'planDepartureAt', width: 170, render: renderTime },
        { title: '计划发车(本地)', dataIndex: 'planDepartureLocalAt', width: 200, render: renderLocalWithTimezone<TmsTaskItem>('planDepartureTimezone') },
        { title: '实际发车(UTC)', dataIndex: 'actualDepartureAt', width: 170, render: renderTime },
        { title: '实际发车(本地)', dataIndex: 'actualDepartureLocalAt', width: 200, render: renderLocalWithTimezone<TmsTaskItem>('actualDepartureTimezone') },
        { title: '计划到达(UTC)', dataIndex: 'planArrivalAt', width: 170, render: renderTime },
        { title: '计划到达(本地)', dataIndex: 'planArrivalLocalAt', width: 200, render: renderLocalWithTimezone<TmsTaskItem>('planArrivalTimezone') },
        { title: '实际到达(UTC)', dataIndex: 'actualArrivalAt', width: 170, render: renderTime },
        { title: '实际到达(本地)', dataIndex: 'actualArrivalLocalAt', width: 200, render: renderLocalWithTimezone<TmsTaskItem>('actualArrivalTimezone') },
        { title: '承运商ID', dataIndex: 'carrierId', width: 100, render: renderText },
        { title: '车辆ID', dataIndex: 'vehicleId', width: 90, render: renderText },
        { title: '车牌', dataIndex: 'licensePlate', width: 110, render: renderText },
        { title: '柜号', dataIndex: 'trailerNumber', width: 120, render: renderText },
        { title: '发布时间(UTC)', dataIndex: 'publishedAt', width: 170, render: renderTime },
        { title: '完成时间(UTC)', dataIndex: 'completedAt', width: 170, render: renderTime },
        {
            title: '备注',
            dataIndex: 'remark',
            width: 160,
            ellipsis: true,
            render: (v: string | null) => (v ? <Tooltip title={v}>{v}</Tooltip> : '-'),
        },
        { title: '创建时间(UTC)', dataIndex: 'createdAt', width: 170, render: renderTime },
        { title: '更新时间(UTC)', dataIndex: 'updatedAt', width: 170, render: renderTime },
        {
            title: '操作',
            key: 'action',
            width: 180,
            fixed: 'right',
            render: (_, record) => (
                <>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setWaypointTask({ id: record.id, taskNumber: record.taskNumber, businessType: record.businessType, taskStatus: record.taskStatus, routeType: record.routeType })}
                    >
                        查看途经点
                    </Button>
                    <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_TMS]}>
                        <Button type="link" size="small" onClick={() => setEditTarget({ id: record.id, taskNumber: record.taskNumber })}>
                            编辑字段
                        </Button>
                    </HasResource>
                </>
            ),
        },
    ];

    const rows = data?.opsToolTmsTasks.content ?? [];
    const totalElements = data?.opsToolTmsTasks.totalElements ?? 0;

    return (
        <div className="space-y-4">
            <Form form={form}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="id" label="主键ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskNumber" label="任务号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="routeId" label="路线ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskStatus" label="任务状态">
                            <Select allowClear placeholder="全部" options={TASK_STATUS_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskType" label="任务类型">
                            <Select allowClear placeholder="全部" options={toOptions(TASK_TYPE_MAP)} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="businessType" label="业务类型">
                            <Select allowClear placeholder="全部" options={toOptions(BUSINESS_TYPE_MAP)} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="isDeleted" label="是否删除">
                            <Select allowClear placeholder="全部" options={IS_DELETED_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="taskDateRange" label="任务日期">
                            <RangePicker allowEmpty={[true, true]} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col span={12} className="text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<TmsTaskItem>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 4660 }}
                className="mb-4"
            />
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
            <TaskWaypointDrawer task={waypointTask} onClose={() => setWaypointTask(null)} />
            <TaskFieldEditDrawer
                target={editTarget}
                onClose={() => setEditTarget(null)}
                onSuccess={() => setPagination((prev) => ({ ...prev }))}
            />
        </div>
    );
};

export default TaskManagementTab;
